<script>
import { layoutMethods, puhunanOffersMethods, puhunanOffersComputed } from "@/state/helpers";
import moment from "moment";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import Swal from "sweetalert2";

// KPI cards `sub` renders the small line under the
// value; `tone` colors it (up = green trend, muted = grey, brand = purple).
const CARDS = [
  {
    key: "merchantsTargeted",
    title: "Merchants Targeted",
    icon: "mdi-account-group",
    format: "number",
    subIcon: "mdi-information-outline",
    subTone: "brand",
    subText: () => "Total merchants targeted",
  },
  {
    key: "offersReceived",
    title: "Offers Received",
    icon: "mdi-email-check-outline",
    format: "number",
    subIcon: "mdi-trending-up",
    subTone: "secondary",
    subText: (k) => `${Number(k.offerRatePct || 0).toFixed(1)}% Offer Rate`,
  },
  {
    key: "activeOffers",
    title: "Active Offers",
    icon: "mdi-account-check",
    iconTone: "success",
    format: "number",
    subIcon: "mdi-trending-up",
    subTone: "up",
    subText: (k) => `${Number(k.activeRatePct || 0).toFixed(1)}% Active Rate`,
  },
  {
    key: "totalOfferAmount",
    title: "Total Offer Amount",
    icon: "mdi-cash-multiple",
    format: "compactPeso",
    subIcon: "mdi-chart-box-outline",
    subTone: "brand",
    subText: (k) => `PHP ${new Intl.NumberFormat("en-US").format(Math.round(k.averageOffer || 0))} average offer`,
  },
];

const STATUS_OPTIONS = [
  { value: "", text: "All" },
  { value: "active", text: "Active" },
  { value: "inactive", text: "Inactive" },
];

// b-table fields (house pattern). `type` drives the cell-formatting template.
// merchantName is unsortable: it's resolved from other DBs after the query.
const COLUMNS = [
  { key: "merchantId", label: "Merchant ID", sortable: true },
  { key: "merchantName", label: "Merchant Name", sortable: false, thClass: "po-th-nosort" },
  { key: "offerAmount", label: "Offer Amount", sortable: true, type: "peso", thClass: "text-end", tdClass: "text-end" },
  { key: "merchantStatus", label: "Merchant Status", sortable: true, type: "status" },
  { key: "loanStatus", label: "Loan Status", sortable: true, type: "status" },
  { key: "dateOfferReceived", label: "Date Offer Received", sortable: true, type: "date" },
  { key: "offerEndDate", label: "Offer End Date", sortable: true, type: "date" },
];

export default {
  page: {
    title: "Puhunan Offers",
  },
  components: {
    flatPickr,
  },
  data() {
    return {
      isLoading: false,
      cardDefs: CARDS,
      columns: COLUMNS,
      statusOptions: STATUS_OPTIONS,
      // filter inputs
      searchKeyword: "",
      loanStatus: "",
      dateRange: "",
      // Single range input, e.g. "2023-02-01 to 2023-03-01".
      rangeConfig: {
        mode: "range",
        dateFormat: "Y-m-d",
        maxDate: moment().format("YYYY-MM-DD"),
        // Range mode leaves the input blank when only one date is picked and the
        // popup closes. Complete it as a same-day range so the field shows the
        // selection (and v-model stays in sync with what's sent to the API).
        onClose: (selectedDates, _dateStr, instance) => {
          if (selectedDates.length === 1) {
            instance.setDate([selectedDates[0], selectedDates[0]], true);
          }
        },
      },
      // applied filter (what the table reflects)
      applied: { search: "", loanStatus: "", from: "", to: "" },
      currentPage: 1,
      perPage: 6,
      pageOptions: [6, 10, 20, 50, 100],
      // Server-side sort: sortBy is the column key sent to the API, orderDesc the direction.
      sortBy: "dateOfferReceived",
      sortDesc: true,
      isExporting: false,
      hasLoadedOnce: false,
    };
  },
  computed: {
    ...puhunanOffersComputed,

    kpi() {
      return this.offerKpi || {};
    },
    rows() {
      return this.offerRows || [];
    },
    totalRecords() {
      return this.offerPageInfo?.total || 0;
    },
    cardList() {
      const k = this.kpi || {};
      return this.cardDefs.map((def) => ({
        ...def,
        value: k[def.key],
        sub: def.subText(k),
      }));
    },
  },
  methods: {
    ...layoutMethods,
    ...puhunanOffersMethods,

    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "0";
      return new Intl.NumberFormat("en-US").format(value);
    },
    // Whole pesos, no decimals — e.g. "PHP 2,000".
    formatPeso(value) {
      return `PHP ${new Intl.NumberFormat("en-US").format(Math.round(Number(value) || 0))}`;
    },
    // Abbreviated for the KPI card: 42500000 -> "PHP 42.5M".
    formatCompactPeso(value) {
      const n = Number(value) || 0;
      if (Math.abs(n) >= 1e9) return `PHP ${(n / 1e9).toFixed(1)}B`;
      if (Math.abs(n) >= 1e6) return `PHP ${(n / 1e6).toFixed(1)}M`;
      if (Math.abs(n) >= 1e3) return `PHP ${(n / 1e3).toFixed(1)}K`;
      return this.formatPeso(n);
    },
    formatCardValue(card) {
      const v = card.value ?? 0;
      if (card.format === "compactPeso") return this.formatCompactPeso(v);
      return this.formatNumber(v);
    },
    formatDate(v) {
      if (!v) return "--";
      const m = moment(v);
      return m.isValid() ? m.format("MMMM D, YYYY [at] hh:mm A") : v;
    },
    capitalize(s) {
      const str = String(s || "");
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    // Status is coloured text in the design, not a pill.
    statusClass(v) {
      return String(v || "").toLowerCase() === "active" ? "po-status-active" : "po-status-inactive";
    },
    // Server-side sort: b-table emits ctx with sortBy/sortDesc; jump to page 1 and refetch.
    // merchantName is resolved from other DBs after the query, so it can't be sorted in SQL — ignore it.
    onSortChanged(ctx) {
      if (ctx.sortBy === "merchantName") return;
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.currentPage = 1;
      this.fetchList();
    },
    // flatpickr range mode emits "YYYY-MM-DD to YYYY-MM-DD" (single date until
    // the second is picked), so split on " to " and reuse from for both.
    splitRange() {
      if (!this.dateRange) return { from: "", to: "" };
      const [from, to] = this.dateRange.split(" to ");
      return { from: from || "", to: to || from || "" };
    },

    async fetchList() {
      this.isLoading = true;
      try {
        const res = await this.getPuhunanOffersList({
          page: this.currentPage,
          rows: this.perPage,
          search: this.applied.search || undefined,
          loanStatus: this.applied.loanStatus || undefined,
          from: this.applied.from || undefined,
          to: this.applied.to || undefined,
          sort: this.sortBy,
          orderDesc: this.sortDesc,
        });
        if (res && res.status !== "ok") {
          Swal.fire({ icon: "error", title: "Unable to load offers", text: res.message });
        }
      } catch (error) {
        console.error("Failed to load Puhunan offers:", error);
        Swal.fire({ icon: "error", title: "Unable to load offers", text: "Please try again." });
      } finally {
        this.isLoading = false;
        this.hasLoadedOnce = true;
      }
    },
    async fetchKpi() {
      try {
        const res = await this.getPuhunanOffersKPI({});
        if (res && res.status !== "ok") console.warn("KPI load returned:", res.message);
      } catch (error) {
        console.error("Failed to load Puhunan offers KPI:", error);
      }
    },
    applyFilter() {
      const { from, to } = this.splitRange();
      this.applied = {
        search: this.searchKeyword.trim(),
        loanStatus: this.loanStatus,
        from,
        to,
      };
      this.currentPage = 1;
      this.fetchList();
    },
    // Clears every filter and reloads.
    refresh() {
      this.searchKeyword = "";
      this.loanStatus = "";
      this.dateRange = "";
      this.applied = { search: "", loanStatus: "", from: "", to: "" };
      this.sortBy = "dateOfferReceived";
      this.sortDesc = true;
      this.currentPage = 1;
      this.fetchKpi();
      this.fetchList();
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.fetchList();
    },
    // Exports the full filtered set (all pages) — the API emails an xlsx link.
    async onExport() {
      if (this.isExporting) return;
      if (!this.totalRecords) {
        Swal.fire({ icon: "info", title: "Nothing to export", text: "No records for the selected filters." });
        return;
      }

      // The API emails the xlsx to the logged-in user (same pattern as other exports).
      let email;
      try {
        email = JSON.parse(localStorage.getItem("userData"))?.email;
      } catch (e) {
        email = null;
      }
      if (!email) {
        Swal.fire({ icon: "error", title: "Export failed", text: "Could not determine your email. Please re-login and try again." });
        return;
      }

      this.isExporting = true;
      try {
        const res = await this.exportPuhunanOffers({
          email,
          search: this.applied.search || undefined,
          loanStatus: this.applied.loanStatus || undefined,
          from: this.applied.from || undefined,
          to: this.applied.to || undefined,
          sort: this.sortBy,
          orderDesc: this.sortDesc,
        });
        if (res && res.status === "ok") {
          Swal.fire({ icon: "success", title: "Export started", text: res.message });
        } else {
          Swal.fire({ icon: "error", title: "Export failed", text: (res && res.message) || "Please try again." });
        }
      } catch (error) {
        console.error("Failed to export Puhunan offers:", error);
        Swal.fire({ icon: "error", title: "Export failed", text: "Please try again." });
      } finally {
        this.isExporting = false;
      }
    },
  },
  async mounted() {
    this.changeTitle({ title: "Puhunan Offers" });
    this.changeBreadCrumb({
      breadCrumbs: [{ text: "Peddlr" }, { text: "Reports" }, { text: "Puhunan Offers" }],
    });
    await Promise.all([this.fetchKpi(), this.fetchList()]);
  },
};
</script>

<template>
  <div class="container-fluid po-page">
    <!-- KPI Cards -->
    <div class="po-bento mb-3">
      <div v-for="card in cardList" :key="card.key" class="po-card po-metric">
        <div class="po-metric-head">
          <span class="po-metric-title">{{ card.title }}</span>
          <i :class="['mdi', card.icon, 'po-metric-icon', card.iconTone ? 'is-' + card.iconTone : '']" aria-hidden="true"></i>
        </div>
        <div class="po-metric-value">
          <span v-if="isLoading && !hasLoadedOnce" class="po-skeleton"></span>
          <template v-else>{{ formatCardValue(card) }}</template>
        </div>
        <div :class="['po-metric-sub', 'is-' + card.subTone]">
          <i :class="['mdi', card.subIcon]" aria-hidden="true"></i>
          <span>{{ card.sub }}</span>
        </div>
      </div>
    </div>

    <!-- Records -->
    <div class="po-card">
      <!-- header: record count + refresh -->
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h5 class="po-records-count mb-0">{{ formatNumber(totalRecords) }} Record(s)</h5>
        <button class="po-btn-refresh" :disabled="isLoading" @click="refresh">
          <i :class="isLoading ? 'bx bx-loader-alt bx-spin' : 'mdi mdi-refresh'"></i>
          <span>Refresh</span>
        </button>
      </div>

      <!-- filter row: split keyword search + status + date range + search -->
      <div class="po-filter-row">
        <div class="po-field">
          <div class="po-search-group">
            <span class="po-search-label" aria-hidden="true">Search All</span>
            <input
              id="po-search-keyword"
              v-model="searchKeyword"
              type="text"
              class="po-search-input"
              placeholder=""
              aria-label="Search keyword"
              @keyup.enter="applyFilter"
            />
          </div>
          <span class="po-field-sub">Search Keyword</span>
        </div>

        <div class="po-field">
          <select v-model="loanStatus" class="po-select" aria-label="Filter by status">
            <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
          </select>
          <span class="po-field-sub">Filter by Status</span>
        </div>

        <div class="po-field">
          <flat-pickr
            v-model="dateRange"
            :config="rangeConfig"
            class="po-date-field"
            placeholder="Select date range"
            aria-label="Filter by date"
          />
          <span class="po-field-sub">Filter by Date</span>
        </div>

        <div class="po-field">
          <button class="po-btn-search" :disabled="isLoading" @click="applyFilter">
            <i class="mdi mdi-magnify"></i>
            <span>Search</span>
          </button>
        </div>
      </div>

      <!-- export -->
      <div class="mb-3">
        <button class="po-btn-export" :disabled="isExporting || isLoading" @click="onExport">
          <i :class="isExporting ? 'bx bx-loader-alt bx-spin' : 'bx bx-upload'"></i>
          <span>{{ isExporting ? 'Exporting…' : 'Export' }}</span>
        </button>
      </div>

      <div class="table-responsive po-table-scroll">
        <div class="table align-middle table-nowrap">
          <b-table
            :items="rows"
            :fields="columns"
            :busy="isLoading"
            responsive="sm"
            :current-page="currentPage"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :no-local-sorting="true"
            show-empty
            class="po-table table-check"
            @sort-changed="onSortChanged"
          >
            <template #table-busy>
              <div class="po-state">
                <i class="bx bx-loader-alt bx-spin me-2"></i>Loading offers…
              </div>
            </template>

            <template #empty>
              <div class="po-state">
                <i class="mdi mdi-file-search-outline me-2"></i>
                No loan offers found for the selected filters.
              </div>
            </template>

            <template #cell(merchantName)="data">
              <p class="text-body m-0">{{ data.item.merchantName || '--' }}</p>
            </template>

            <template #cell(offerAmount)="data">
              <p class="text-body m-0 text-end">{{ formatPeso(data.item.offerAmount) }}</p>
            </template>

            <template #cell(merchantStatus)="data">
              <span :class="statusClass(data.item.merchantStatus)">
                {{ data.item.merchantStatus ? capitalize(data.item.merchantStatus) : '--' }}
              </span>
            </template>

            <template #cell(loanStatus)="data">
              <span :class="statusClass(data.item.loanStatus)">
                {{ data.item.loanStatus ? capitalize(data.item.loanStatus) : '--' }}
              </span>
            </template>

            <template #cell(dateOfferReceived)="data">
              <p class="text-body m-0">{{ formatDate(data.item.dateOfferReceived) }}</p>
            </template>

            <template #cell(offerEndDate)="data">
              <p class="text-body m-0">{{ formatDate(data.item.offerEndDate) }}</p>
            </template>
          </b-table>
        </div>
      </div>

      <!-- Footer: display size + pagination (shared BootstrapVue controls) -->
      <div class="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
        <div class="d-flex align-items-center gap-2">
          <label for="po-display-record" class="po-display-label mb-0">Display</label>
          <b-form-select
            id="po-display-record"
            class="form-select shadow-none w-fit po-display-select"
            v-model="perPage"
            :options="pageOptions"
            @change="onPerPageChange"
          ></b-form-select>
        </div>

        <b-pagination
          v-if="totalRecords > perPage"
          class="pagination-rounded mb-0"
          v-model="currentPage"
          :total-rows="totalRecords"
          :per-page="perPage"
          @input="fetchList"
        ></b-pagination>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Figma variables (node 4504:8813)
   primary #7F4697 / primary-600 #744089 | secondary #E83092 / secondary-700 #A52268
   success-700 #396622 | neutral-300 #868686 | neutral-400 #AFAFAF
   neutral-600 #4B4B4B | neutral-800 #191919 | bg-tertiary #E6EAF1 | neutral-200 #E1E1E1 */
.po-page {
  --po-primary: #7f4697;
  --po-primary-600: #744089;
  --po-secondary: #e83092;
  --po-secondary-700: #a52268;
  --po-success: #396622;
  --po-muted: #868686;
  --po-neutral-400: #afafaf;
  --po-neutral-600: #4b4b4b;
  --po-neutral-800: #191919;
  --po-tertiary: #e6eaf1;
  --po-border: #e1e1e1;
}

.po-card {
  background: #fff;
  border: 1px solid var(--po-border);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

/* ---------- KPI cards: 4 across ---------- */
.po-bento {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.po-metric {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.po-metric-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.po-metric-title {
  font-size: 14px;
  font-weight: 400;
  color: var(--po-neutral-600);
  line-height: 20px;
}

.po-metric-icon {
  font-size: 22px;
  line-height: 1;
  color: var(--po-secondary-700);
  flex-shrink: 0;
}
.po-metric-icon.is-success { color: var(--po-success); }

.po-metric-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 32px;
  color: var(--po-neutral-800);
}

.po-metric-sub {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
}
.po-metric-sub i { font-size: 14px; line-height: 1; }
.po-metric-sub.is-up { color: var(--po-success); }
.po-metric-sub.is-muted { color: var(--po-muted); }
.po-metric-sub.is-brand { color: var(--po-primary-600); }
.po-metric-sub.is-secondary { color: var(--po-secondary-700); }

/* ---------- Filter row (sub-labels centred beneath, per Figma) ---------- */
.po-filter-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.po-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.po-field-sub {
  font-size: 12px;
  color: var(--po-neutral-600);
  text-align: right;
}

/* Split search: field selector fused to the keyword input */
.po-search-group {
  display: inline-flex;
  align-items: stretch;
}

/* Static prefix label ("Merchant") fused to the input to read as one unit. */
.po-search-label {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--po-border);
  border-right: none;
  border-radius: 4px 0 0 4px;
  background: #fff;
  padding: 0 14px;
  height: 40px;
  font-size: 14px;
  color: var(--po-neutral-800);
  white-space: nowrap;
}

.po-search-input {
  border: 1px solid var(--po-border);
  border-radius: 0 4px 4px 0;
  background: #fff;
  padding: 0 14px;
  height: 40px;
  font-size: 14px;
  color: var(--po-neutral-800);
  width: 200px;
}

.po-search-input:focus,
.po-select:focus,
.po-date-field:focus {
  outline: none;
  border-color: var(--po-primary);
  box-shadow: 0 0 0 0.15rem rgba(127, 70, 151, 0.12);
  position: relative;
  float: end;
  z-index: 1;
}

.po-select {
  border: 1px solid var(--po-border);
  border-radius: 4px;
  background: #fff;
  padding: 0 14px;
  height: 40px;
  font-size: 14px;
  color: var(--po-neutral-800);
  min-width: 180px;
}

/* Single range input, matching "2023-02-01 to 2023-03-01" */
.po-date-field {
  border: 1px solid var(--po-border);
  border-radius: 4px;
  background: #fff;
  padding: 0 14px;
  height: 40px;
  font-size: 14px;
  color: var(--po-neutral-800);
  width: 210px;
  cursor: pointer;
}

.po-btn-search {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--po-secondary);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0 20px;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
}
.po-btn-search:hover:not(:disabled) { background: #d02782; }
.po-btn-search:disabled { opacity: 0.65; }
.po-btn-search i { font-size: 18px; line-height: 1; }

.po-btn-refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--po-primary);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0 18px;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
}
.po-btn-refresh:hover:not(:disabled) { background: var(--po-primary-600); }
.po-btn-refresh:disabled { opacity: 0.65; }
.po-btn-refresh i { font-size: 18px; line-height: 1; }

/* ---------- Records header ---------- */
.po-records-count {
  font-size: 20px;
  font-weight: 600;
  color: var(--po-neutral-800);
}

.po-btn-export {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--po-secondary);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0 20px;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
}
.po-btn-export:hover:not(:disabled) { background: #d02782; }
.po-btn-export:disabled { opacity: 0.7; cursor: not-allowed; }
.po-btn-export i { font-size: 18px; line-height: 1; }

/* ---------- Table ---------- */
.po-table-scroll {
  overflow-x: auto;
  padding-bottom: 6px;
  scrollbar-width: thin;
  scrollbar-color: #c9c9c9 #f1f1f1;
}
.po-table-scroll::-webkit-scrollbar { height: 6px; }
.po-table-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 3px; }
.po-table-scroll::-webkit-scrollbar-thumb { background: #c9c9c9; border-radius: 3px; }
.po-table-scroll::-webkit-scrollbar-thumb:hover { background: #b0b0b0; }

.po-table { width: 100%; margin: 0; }

.po-table ::v-deep th {
  font-size: 13px;
  font-weight: 700;
  color: var(--po-neutral-800);
  padding: 12px 16px;
  border-bottom: 1px solid var(--po-tertiary);
  white-space: nowrap;
  background: #fff;
}

.po-table ::v-deep td {
  font-size: 13px;
  font-weight: 400;
  color: var(--po-neutral-800);
  padding: 18px 16px;
  border-bottom: 1px solid var(--po-tertiary);
  white-space: nowrap;
}
.po-table ::v-deep tbody tr:hover td { background: #faf8fb; }

/* Merchant Name is not sortable (resolved after the query) — never show a sort caret or pointer. */
.po-table ::v-deep th.po-th-nosort { cursor: default; }
.po-table ::v-deep th.po-th-nosort > div[role="columnheader"],
.po-table ::v-deep th.po-th-nosort { background-image: none !important; }
.po-table ::v-deep th.po-th-nosort::before,
.po-table ::v-deep th.po-th-nosort::after { display: none !important; }

/* Status as coloured text (design shows no pill) */
.po-status-active { color: var(--po-success); font-weight: 500; }
.po-status-inactive { color: var(--po-muted); }

.po-state {
  color: var(--po-neutral-600);
  font-size: 13px;
  text-align: center;
  padding: 40px 20px !important;
}

.po-skeleton {
  display: inline-block;
  width: 96px;
  height: 30px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0ecf2 25%, #e6e0ea 37%, #f0ecf2 63%);
  background-size: 400% 100%;
  animation: po-shimmer 1.2s ease-in-out infinite;
}
@keyframes po-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

/* ---------- Footer ---------- */
.po-display-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--po-neutral-800);
}

.po-display-select { width: auto; min-width: 72px; }

/* Tint the shared b-pagination active page to the Puhunan primary */
.po-page ::v-deep .pagination-rounded .page-item.active .page-link {
  background-color: var(--po-primary);
  border-color: var(--po-primary);
}

@media (max-width: 1399px) {
  .po-bento { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 767px) {
  .po-bento { grid-template-columns: 1fr; }
}
</style>
